import { fetchWithTransientRetry } from "@/lib/transient-fetch";
import { getSharedRefreshedToken, forceSessionExpiredRedirect } from "@/lib/auth-refresh";
import { rememberCanonicalPayload } from "../../../../lib/copy/src/public-canonical-client.mjs";

async function canonicalJson<T>(response: Response): Promise<T> {
  const body = await response.json();
  rememberCanonicalPayload(body);
  return body;
}

const API_BASE = `${import.meta.env.BASE_URL || "/"}api`;

/**
 * Single-retry wrapper for idempotent-safe availability PUT endpoints.
 *
 * `fetchWithTransientRetry` intentionally skips retries for mutating methods
 * (PUT/POST/DELETE) to prevent double-submits on booking/payment paths.
 * The three availability saves (blocked-dates, availability-window,
 * daily-rates) are replace-style operations (DELETE+INSERT / UPDATE) that are
 * safe to retry because re-running them produces the same DB state. This
 * helper adds one bounded retry on a network-level failure or a 5xx response
 * so transient pool/gateway blips are recovered before surfacing an error.
 */
async function withAvailabilityRetry(fn: () => Promise<Response>): Promise<Response> {
  try {
    const res = await fn();
    if (res.status < 500) return res;
    // One retry on a transient 5xx. `fn` re-issues the full fetch so the
    // response body is fresh on each call — no cloning needed.
    return fn();
  } catch {
    return fn();
  }
}

function getImpersonationHeaders(): Record<string, string> {
  try {
    const raw = sessionStorage.getItem("bac_impersonation");
    if (!raw) return {};
    const data = JSON.parse(raw);
    if (data.expiresAt < Date.now()) return {};
    if (!data.userId || !data.sessionId) return {};
    return {
      "X-Impersonate-User-Id": data.userId,
      "X-Impersonation-Session-Id": data.sessionId,
    };
  } catch {
    return {};
  }
}

function authHeaders(token: string): HeadersInit {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    ...getImpersonationHeaders(),
  };
}

/**
 * Like `authFetch` but runs the underlying `fetch` through
 * `withAvailabilityRetry` so transient 5xx/network failures on the three
 * replace-style availability PUT endpoints get one automatic retry before
 * surfacing an error. Never used for booking/payment paths.
 */
/** Shared helper — builds a typed listings error from a failed response body. */
function buildListingsError(body: Record<string, unknown>, status: number): Error & {
  errors?: Array<{ field: string; message: string }>;
  status?: number;
  detail?: string;
  code?: string;
} {
  const message = body.detail
    ? `${body.error || `API error ${status}`}: ${body.detail}`
    : (body.error || `API error ${status}`);
  const err = new Error(message as string) as Error & {
    errors?: Array<{ field: string; message: string }>;
    status?: number;
    detail?: string;
    code?: string;
  };
  if (Array.isArray(body.errors)) err.errors = body.errors as Array<{ field: string; message: string }>;
  if (typeof body.detail === "string") err.detail = body.detail;
  if (typeof body.code === "string") err.code = body.code;
  err.status = status;
  return err;
}

async function availabilityAuthFetch<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  const fullInit: RequestInit = {
    ...init,
    cache: "no-store" as RequestCache,
    headers: { ...authHeaders(token), ...init?.headers },
  };
  const res = await withAvailabilityRetry(() => fetch(`${API_BASE}${path}`, fullInit));
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    if (res.status === 401) {
      const freshToken = await getSharedRefreshedToken();
      if (freshToken && freshToken !== token) {
        const retryInit: RequestInit = {
          ...init,
          cache: "no-store" as RequestCache,
          headers: { ...authHeaders(freshToken), ...init?.headers },
        };
        const retryRes = await withAvailabilityRetry(() => fetch(`${API_BASE}${path}`, retryInit));
        if (retryRes.ok) return canonicalJson<T>(retryRes);
        const retryBody = await retryRes.json().catch(() => ({}));
        if (retryRes.status === 401 || retryBody.error === "session_expired") {
          forceSessionExpiredRedirect();
        }
        throw buildListingsError(retryBody, retryRes.status);
      }
      // No fresh token — session is truly dead.
      forceSessionExpiredRedirect();
    }
    throw buildListingsError(body, res.status);
  }
  return canonicalJson<T>(res);
}

async function authFetch<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  const res = await fetchWithTransientRetry(`${API_BASE}${path}`, {
    ...init,
    cache: "no-store" as RequestCache,
    headers: { ...authHeaders(token), ...init?.headers },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    // Prefer the API's `detail` (e.g. the underlying Postgres / Supabase
    // message) when present so the toast tells the host what really went
    // wrong instead of a generic "Failed to update listing".
    if (res.status === 401) {
      const freshToken = await getSharedRefreshedToken();
      if (freshToken && freshToken !== token) {
        const retryRes = await fetchWithTransientRetry(`${API_BASE}${path}`, {
          ...init,
          cache: "no-store" as RequestCache,
          headers: { ...authHeaders(freshToken), ...init?.headers },
        });
        if (retryRes.ok) return canonicalJson<T>(retryRes);
        const retryBody = await retryRes.json().catch(() => ({}));
        if (retryRes.status === 401 || retryBody.error === "session_expired") {
          forceSessionExpiredRedirect();
        }
        throw buildListingsError(retryBody, retryRes.status);
      }
      // No fresh token — session is truly dead.
      forceSessionExpiredRedirect();
    }
    // Preserve the API's structured `code` (e.g. "host_phone_unverified")
    // so callers can branch on it without re-parsing the message string.
    throw buildListingsError(body, res.status);
  }
  return canonicalJson<T>(res);
}

export interface LookupAirport {
  id: number;
  name: string;
  iata_code: string;
  city: string | null;
  country: string;
}

export interface LookupData {
  bodies: Array<{ id: number; name: string }>;
  colours: Array<{ id: number; name: string; hex_code: string | null }>;
  eventTypes: Array<{ id: number; name: string; slug: string }>;
  valueRanges: Array<{ id: number; range_label: string; min_value: number; max_value: number }>;
  airports: LookupAirport[];
}

export interface VinResult {
  make: string;
  model: string;
  year: string;
  trim: string;
  bodyClass: string;
  engineCylinders: string;
  engineDisplacement: string;
  transmissionStyle: string;
  driveType: string;
  /** Country of manufacture — derived from the VIN's WMI prefix when
   *  available, otherwise from VIN position 1 as a coarse hint. Empty
   *  string when nothing could be inferred. */
  country: string;
  /** True when NHTSA couldn't return a model — common for non-US
   *  vehicles (LRW Tesla Shanghai, VF1 Renault, ZLA Lancia, SDB TVR,
   *  etc.). The wizard uses this to render the "we need a bit more
   *  information" graceful prompt instead of pretending we found
   *  the car. */
  partial: boolean;
}

export interface OwnerListing {
  readiness?: import("@workspace/listing-requirements").ListingReadiness;
  id: number;
  slug: string;
  /**
   * Canonical brand name resolved from the joined `car_makes` row (#810).
   * `null` when the listing's make_id is unset or points at a deactivated
   * placeholder — the wizard will require the host to pick a verified
   * brand on the next save.
   */
  make: string | null;
  model: string;
  year: number;
  city: string;
  state: string;
  countryCode: string | null;
  descriptionLength: number;
  listingStatus: string;
  stepCompleted: number;
  /**
   * @deprecated Now derived from `listingStatus === "inactive"`. Still
   * present on the wire for backwards compatibility but the UI no longer
   * reads it — the My Garage page derives Live/Paused/etc. from
   * `listingStatus` alone (see `getListingStatusDisplay`).
   */
  disabledByOwner: boolean;
  hideApex: boolean;
  /**
   * Number of bookings the renter is still waiting on the host to accept
   * or decline (status === "pending"). Pending bookings hard-block the
   * delete action — the My Garage confirm dialog tells the host to act on
   * these requests before they can remove the listing.
   */
  pendingBookingCount: number;
  /**
   * Number of already-accepted bookings that haven't completed yet
   * (status in {approved, confirmed, in_progress}). These also hard-block
   * delete — cancelling a confirmed renter has to be done from the trip
   * detail page so the cancellation flow runs end-to-end.
   */
  upcomingBookingCount: number;
  priceDriving: number | null;
  /** ISO currency code for `priceDriving`. The api-server
   *  `/listings/my` payload populates this from the listing's
   *  `local_currency_code`. May be null on legacy listings — UI should
   *  fall back to "USD" when null so the symbol stays sensible. */
  localCurrencyCode?: string | null;
  rentalDrivingActive: boolean;
  rentalChauffeurActive: boolean;
  rentalEventActive: boolean;
  /**
   * Task #6383 — true when the listing has Instant Book activated
   * (`cars.instant_rental`). Drives the "Instant Book" chip on the My
   * Garage card. Optional so cached payloads from older builds parse
   * cleanly; treat missing as false.
   */
  instantBookActive?: boolean;
  reviewsRating: number | null;
  reviewsCount: number;
  apexScore: number | null;
  standoutScore: import("@/lib/api").StandoutScoreData | null;
  primaryImageUrl: string | null;
  photoCount: number;
  createdAt: string;
  updatedAt: string;
  /**
   * Task #2256 — true when the owner's most recent edit on this live
   * listing was AI-flagged and held for admin review. The listing
   * stays live with the prior approved values; the badge tells the
   * owner their changes are not yet visible.
   */
  pendingChange?: boolean;
  /**
   * Task #227 — set when the listing's make is awaiting admin verification
   * via a pending `car_make_requests` row. Drives the "Brand pending review"
   * hint on the My Garage card so hosts know why their listing is in
   * awaiting_approval without having to contact support.
   */
  pendingMakeRequestId?: number | null;
}

export interface CarImage {
  id: number;
  is_active?: boolean;
  url: string;
  r2_path: string | null;
  is_primary: boolean;
  display_order: number;
  cdn_thumbnail?: string | null;
  cdn_medium?: string | null;
  cdn_full?: string | null;
  editor_proxy_url?: string | null;
}

/**
 * Cloudflare Stream-backed listing video (task: video listings v1).
 *
 * Lives in a separate table from CarImage so the hero-image / primary-photo
 * invariants stay untouched. `playback_url` is the iframe.videodelivery.net
 * embed URL — only populated once `status === "ready"`.
 */
export interface CarVideo {
  id: number;
  stream_uid: string;
  /** pendingupload | queued | inprogress | downloading | ready | error */
  status: string;
  duration_seconds: number | null;
  thumbnail_url: string | null;
  display_order: number;
  is_active: boolean;
  /** Set once Cloudflare finishes transcoding (status === "ready"). */
  playback_url: string | null;
}

export interface ListingDetail {
  editProposal?: { proposalId: number; reviewState: string; error?: string | null } | null;
  changeRequest?: { id: number; notes: string | null; requestedAt: string; revision: string; awaitingOwner?: boolean } | null;
  car: Record<string, unknown> & {
    /** Server policy: whether this listing still needs an explicit US host valuation choice. */
    valuation_choice_required?: boolean;
  };
  images: CarImage[];
  blockedDates: Array<{ id: number; start_date: string; end_date: string; reason: string }>;
  dailyRates: Array<{ id: number; date: string; price: number }>;
  availabilityWindow: {
    available_from: string | null;
    available_to: string | null;
    advance_notice_days: number;
    min_booking_days: number;
  } | null;
  eventTypeIds: number[];
  deliveryAirports: Array<{ id: number; airport_code: string; airport_name: string; flat_fee: number }>;
  instantBookDates: Array<{ id: number; start_date: string; end_date: string }>;
  standoutScore: import("@/lib/api").StandoutScoreData | null;
  /**
   * Server-confirmed `car_make_requests.id` when the draft's make is awaiting
   * verification (task #5073) — only present while the linked request is still
   * `status='pending'`. Lets the wizard render the "make pending review" panel
   * deterministically on resume instead of relying on the in-memory registry.
   */
  pendingMakeRequestId?: number | null;
}

export interface OwnerBlockedDate {
  id: number;
  car_id: number;
  start_date: string;
  end_date: string;
  reason: string;
}

export interface BrandFeedItem {
  id: number;
  canonical_name: string;
  logo_slug: string | null;
}

export interface IncompleteListing {
  id: number;
  make: string | null;
  model: string;
  year: number;
  primaryImageUrl: string | null;
  updatedAt: string;
}

export const listingsApi = {
  getReadiness: (id: number, token: string) =>
    authFetch<import("@workspace/listing-requirements").ListingReadiness>(`/listings/${id}/readiness`, token),
  getIncompleteListings: (token: string) =>
    authFetch<{ listings: IncompleteListing[] }>("/listings/incomplete", token),

  getLookup: () =>
    fetch(`${API_BASE}/listings/lookup`).then(r => r.json()) as Promise<LookupData>,

  /**
   * Verified-brand registry feed. Called once on App boot so the wizard /
   * edit-page validators in `@workspace/listing-requirements` can resolve
   * makes synchronously. `car_makes` is small (~210 active rows) so a single
   * unfiltered fetch is cheaper than paginating.
   *
   * Also returns the active `car_make_requests` queue (`pending`) so the
   * wizard's vehicle-identity step can recognise a draft whose make is
   * awaiting admin verification (task #1547). Pending list is typically
   * 0–10 rows so there's no pagination here either.
   */
  getBrands: () =>
    fetch(`${API_BASE}/listings/brands`).then(r => {
      if (!r.ok) throw new Error(`Brand feed failed (${r.status})`);
      return r.json();
    }) as Promise<{
      brands: BrandFeedItem[];
      pending: Array<{ id: number; original_text: string; normalised_name: string | null }>;
    }>,

  /**
   * Submit a "request a new make" ticket from the wizard. Server coalesces
   * by normalised name so concurrent hosts requesting the same brand share
   * one pending row. Used by the inline CTA on SubStepVehicleIdentity when
   * the typed make doesn't match a verified brand (task #1547).
   */
  requestBrand: (
    token: string,
    make: string,
    opts?: { note?: string; draftCarId?: number | null },
  ) =>
    authFetch<{
      already_verified: boolean;
      canonical?: string;
      request?: {
        id: number;
        status: "pending" | "approved" | "rejected";
        original_text: string;
        normalised_name: string | null;
        created_at: string;
      };
    }>("/listings/brands/request", token, {
      method: "POST",
      body: JSON.stringify({
        make,
        ...(opts?.note ? { note: opts.note } : {}),
        // Linking the request to the host's draft up front means resume +
        // admin moderate see the connection without an extra round-trip.
        // Server ownership-checks before mirroring `cars.pending_make_request_id`
        // (#1547).
        ...(opts?.draftCarId != null && Number.isFinite(opts.draftCarId)
          ? { draftCarId: opts.draftCarId }
          : {}),
      }),
    }),

  searchAirports: (
    opts: { q?: string; country?: string; lat?: number; lng?: number; limit?: number; signal?: AbortSignal } = {},
  ) => {
    const params = new URLSearchParams();
    if (opts.q) params.set("q", opts.q);
    if (opts.country) params.set("country", opts.country);
    if (typeof opts.lat === "number" && Number.isFinite(opts.lat)) params.set("lat", String(opts.lat));
    if (typeof opts.lng === "number" && Number.isFinite(opts.lng)) params.set("lng", String(opts.lng));
    params.set("limit", String(opts.limit ?? 25));
    return fetch(`${API_BASE}/listings/airports?${params}`, { signal: opts.signal }).then(r => {
      if (!r.ok) throw new Error(`Airport search failed (${r.status})`);
      return r.json();
    }) as Promise<{ airports: LookupAirport[] }>;
  },

  getMyBlockedDates: (token: string) =>
    authFetch<{ blockedDates: OwnerBlockedDate[] }>("/listings/my/blocked-dates", token),

  getModels: (make: string) =>
    fetch(`${API_BASE}/listings/models?make=${encodeURIComponent(make)}`).then(r => r.json()) as Promise<{ models: string[] }>,

  decodeVin: (vin: string) =>
    fetch(`${API_BASE}/listings/vin/${vin}`).then(r => {
      if (!r.ok) throw new Error("VIN decode failed");
      return r.json() as Promise<VinResult>;
    }),

  getMyListings: (token: string) =>
    authFetch<{ listings: OwnerListing[] }>("/listings/my", token),

  getListing: (id: number, token: string, signal?: AbortSignal) =>
    authFetch<ListingDetail>(`/listings/${id}`, token, signal ? { signal } : undefined),

  prepareForEnrichment: (token: string, data: Record<string, unknown>) =>
    authFetch<{ carId: number; slug: string | null }>("/ai-enrichment/prepare", token, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  createListing: (token: string, data: Record<string, unknown>) =>
    authFetch<{ id: number; slug: string }>("/listings", token, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  updateListing: (id: number, token: string, data: Record<string, unknown>) =>
    authFetch<{ success: boolean; contentFlagged?: boolean; proposal?: { proposalId: number; reviewState: string } | null; reviewState?: string; slug?: string | null; warnings?: Array<{ field: string; message: string; severity?: string }> }>(`/listings/${id}`, token, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  // `fields` is OPTIONAL defence-in-depth (#1552). Callers may pass the
  // visible vehicle-identity (and optionally pricing) payload so the server's
  // /submit endpoint can canonicalise the make and mirror cars.make_id in the
  // same atomic call before its strict submit gate runs. Eliminates the
  // PUT-then-/submit race that surfaced as recurring "Make is required"
  // failures on resumed drafts. Server treats a missing/empty body as a
  // legacy call and behaves exactly as before.
  // Task #1635. Records the host's non-US self-insurance attestation.
  // Server captures the user id from the session and snapshots the
  // listing's CURRENT country_code at the moment of the call — that
  // snapshot is what the submit gate compares against to detect a
  // stale attestation when the host later changes country. Returns the
  // persisted timestamp + country snapshot so the client can keep its
  // local state in sync without a follow-up GET.
  attestSelfInsurance: (id: number, token: string, countryCode?: string) =>
    authFetch<{
      success: boolean;
      self_insurance_attestation_at: string;
      self_insurance_attestation_by: number;
      self_insurance_attestation_country_code: string;
    }>(`/listings/${id}/self-insurance-attestation`, token, {
      method: "PUT",
      ...(countryCode
        ? { body: JSON.stringify({ country_code: countryCode }) }
        : {}),
    }),

  submitListing: (id: number, token: string, fields?: Record<string, unknown>) =>
    authFetch<{ success: boolean }>(`/listings/${id}/submit`, token, {
      method: "PUT",
      ...(fields && Object.keys(fields).length > 0
        ? { body: JSON.stringify(fields) }
        : {}),
    }),

  saveDraftListing: (id: number, token: string) =>
    authFetch<{ success: boolean }>(`/listings/${id}/save-draft`, token, { method: "PUT" }),

  getModerationStatus: (id: number, token: string) =>
    authFetch<{
      listing_status: string;
      moderation: {
        status: string;
        text_flags: Array<{ category: string; severity: string; detail: string; field?: string }>;
        image_flags: Array<{ category: string; severity: string; detail: string; imageId?: number }>;
        checked_at: string;
      } | null;
    }>(`/listings/${id}/moderation-status`, token),

  requestManualReview: (id: number, token: string) =>
    authFetch<{ success: boolean }>(`/listings/${id}/request-review`, token, { method: "POST" }),

  // Task #6370 — expiry auto-pause banner. `getAutoPausedStatus` returns the
  // listings the system paused after an unanswered booking request expired
  // (and that are still paused); `reactivateAutoPaused` is the banner's bulk
  // "Reactivate listings" action — it only ever touches auto-paused cars,
  // never manually paused ones.
  getAutoPausedStatus: (token: string) =>
    authFetch<{ autoPaused: boolean; carIds: number[] }>("/listings/my/auto-paused", token),

  reactivateAutoPaused: (token: string) =>
    authFetch<{ success: boolean; restored: number[]; skipped: Array<{ carId: number; reason: string }> }>(
      "/listings/my/reactivate-auto-paused",
      token,
      { method: "POST" },
    ),

  pauseListing: (id: number, token: string) =>
    authFetch<{ success: boolean; listingStatus: "active" | "inactive"; disabledByOwner: boolean }>(
      `/listings/${id}/pause`,
      token,
      { method: "PUT" },
    ),

  toggleApex: (id: number, token: string) =>
    authFetch<{ success: boolean; hideApex: boolean }>(`/listings/${id}/toggle-apex`, token, { method: "PUT" }),

  // BL-017 (revised): hosts may permanently remove their own REJECTED and
  // DRAFT (incomplete / ready_to_submit) listings — these have no other
  // removal path (Pause only applies to live/paused listings). Any other
  // status still returns 405 `host_delete_not_allowed` from the server.
  // Live/paused hosts take a listing offline via `pauseListing`
  // (reversible); anything with bookings is admin-only. See
  // docs/user-flows/listing-edit-and-moderation.md §"Host delete is retired".
  removeListing: (id: number, token: string) =>
    authFetch<{ success: boolean; listing_status: "deleted"; deleted_at: string }>(
      `/listings/${id}`,
      token,
      { method: "DELETE" },
    ),

  requestPhotoUploadUrl: (carId: number, token: string, contentType: string) =>
    authFetch<{ uploadURL: string; objectPath: string; storage: string }>(
      `/listings/${carId}/photos/request-upload-url`,
      token,
      { method: "POST", body: JSON.stringify({ contentType }) }
    ),

  uploadPhoto: async (carId: number, token: string, blob: Blob, meta: { is_primary: boolean; display_order: number }): Promise<CarImage> => {
    const params = new URLSearchParams({
      is_primary: String(meta.is_primary),
      display_order: String(meta.display_order),
    });
    const url = `${API_BASE}/listings/${carId}/photos/upload?${params}`;
    // Send as multipart/form-data so the request passes Cloudflare WAF rules.
    // Raw image/jpeg POST bodies can trigger bot-challenge 403s on the custom
    // domain. Standard multipart (the same format HTML <input type="file">
    // uses) is allowed through. The browser sets the boundary automatically
    // when we leave Content-Type unset on a FormData body.
    const body = new FormData();
    body.append("photo", blob, "photo.jpg");
    const res = await fetch(url, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body,
    });
    if (!res.ok) {
      const errBody = await res.text().catch(() => "");
      throw new Error(`Upload failed: ${res.status} ${errBody}`);
    }
    return res.json();
  },

  reorderPhotos: (carId: number, token: string, order: number[]) =>
    authFetch<{ success: boolean }>(`/listings/${carId}/photos/reorder`, token, {
      method: "PUT",
      body: JSON.stringify({ order }),
    }),

  setPrimaryPhoto: (carId: number, photoId: number, token: string) =>
    authFetch<{ success: boolean }>(`/listings/${carId}/photos/${photoId}/primary`, token, { method: "PUT" }),

  deletePhoto: (carId: number, photoId: number, token: string) =>
    authFetch<{ success: boolean }>(`/listings/${carId}/photos/${photoId}`, token, { method: "DELETE" }),

  /* -------------------------------------------------------------------- */
  /* Cloudflare Stream video uploads (task: video listings v1).           */
  /*                                                                      */
  /* Two-step upload:                                                     */
  /*   1. requestVideoUpload → server mints a direct-creator-upload URL   */
  /*      and inserts a `cars_videos` row in `pendingupload`.             */
  /*   2. PUT the file body straight to Cloudflare's `uploadURL`. The     */
  /*      api-server never sees the bytes — keeps egress off our server.  */
  /*   3. Poll refreshVideoStatus every ~3s until status === "ready" or   */
  /*      "error" or we hit the 90s timeout (transcode usually takes      */
  /*      <30s for a 60s clip).                                           */
  /* -------------------------------------------------------------------- */

  listVideos: (carId: number, token: string, signal?: AbortSignal) =>
    authFetch<{ videos: CarVideo[] }>(`/listings/${carId}/videos`, token, { signal }),

  requestVideoUpload: (carId: number, token: string, signal?: AbortSignal) =>
    authFetch<
      CarVideo & {
        uploadURL: string;
        maxDurationSeconds: number;
        maxSizeBytes: number;
      }
    >(`/listings/${carId}/videos/upload-url`, token, { method: "POST", signal }),

  refreshVideoStatus: (carId: number, videoId: number, token: string, signal?: AbortSignal) =>
    authFetch<CarVideo>(
      `/listings/${carId}/videos/${videoId}/refresh-status`,
      token,
      { method: "POST", signal },
    ),

  deleteVideo: (carId: number, videoId: number, token: string, signal?: AbortSignal) =>
    authFetch<{ success: boolean }>(
      `/listings/${carId}/videos/${videoId}`,
      token,
      { method: "DELETE", signal },
    ),

  updateBlockedDates: (
    carId: number,
    token: string,
    dates: Array<{ start_date: string; end_date: string }>,
    expectedDates: string[],
  ) =>
    availabilityAuthFetch<{ success: boolean; skippedDates?: string[] }>(`/listings/${carId}/blocked-dates`, token, {
      method: "PUT",
      body: JSON.stringify({ dates, expectedDates }),
    }),

  updateAvailabilityWindow: (carId: number, token: string, data: Record<string, unknown>) =>
    availabilityAuthFetch<{ success: boolean }>(`/listings/${carId}/availability-window`, token, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  updateAdvanceNotice: (carId: number, token: string, days: number) =>
    availabilityAuthFetch<{ success: boolean }>(`/listings/${carId}/advance-notice`, token, {
      method: "PATCH",
      body: JSON.stringify({ advance_notice_days: days }),
    }),

  updateDailyRates: (
    carId: number,
    token: string,
    rates: Array<{ date: string; price: number }>,
    expectedRates: Array<{ date: string; price: number }>,
  ) =>
    availabilityAuthFetch<{ success: boolean; rates?: Array<{ date: string; price: number }> }>(`/listings/${carId}/daily-rates`, token, {
      method: "PUT",
      body: JSON.stringify({ rates, expectedRates }),
    }),

  enrichDescription: (carId: number, token: string) =>
    authFetch<{ description: string }>(`/listings/${carId}/enrich-description`, token, {
      method: "POST",
    }),

  triggerAiEnrichment: (carId: number, token: string) =>
    authFetch<{ success: boolean; message: string }>("/ai-enrichment/run", token, {
      method: "POST",
      body: JSON.stringify({ carId }),
    }),

  getEnrichmentProgress: (carId: number, token: string) =>
    authFetch<{
      status: "running" | "complete" | "error" | "not_started";
      phase: "specs" | "apex" | "done" | null;
      progress: number;
      specFields?: string[];
      apexCategories?: string[];
      error?: string;
    }>(`/ai-enrichment/progress?carId=${carId}`, token),

  getApexScores: (carId: number, token: string) =>
    authFetch<{
      score: import("@/lib/api").StandoutScoreData;
      enrichedSpecs: Record<string, unknown>;
      valuation?: {
        valuationEstimate: number | null;
        suggestedDailyRate: number | null;
        breakdown: {
          estimatedValue: number;
          suggestedDailyRate: number;
          priceLow: number;
          priceHigh: number;
          priceMedian: number;
          priceMean: number;
          compsFound: number;
          exactYearComps: number;
          adjustmentExcellent: number;
          adjustmentExcellentReason: string | null;
          adjustmentRisky: number;
          adjustmentRiskyReason: string | null;
          estimateSource?: "auction_exact" | "auction_widened" | "auction_make_only" | "auction_raw" | "platform_comps" | "tier_table" | "ai_fallback";
          estimateConfidence?: "high" | "medium" | "low";
          canonicalMake?: string;
          sourceLabel?: string;
        } | null;
      };
    }>(
      `/ai-enrichment/apex-scores/${carId}`,
      token,
    ),

  getVehiclePerformance: (carId: number, token: string) =>
    authFetch<{
      totalRevenue: number;
      thisMonthRevenue: number;
      lastMonthRevenue: number;
      avgBookingValue: number;
      completedBookings: number;
      cancelledBookings: number;
      declinedBookings: number;
      upcomingBookings: number;
      pendingBookings: number;
      totalInquiries: number;
      conversionRate: number;
      monthlyBreakdown: Array<{ month: string; revenue: number; bookings: number }>;
      recentBookings: Array<{ id: number; completedAt: string; ownerEarn: number; tripType: string; currency: string }>;
      currency: string;
      listingSince: string;
    }>(`/bookings/vehicle-performance/${carId}`, token),

  getSuggestedRate: (carId: number, token: string) =>
    authFetch<{
      // Unified pricing fields (task #1540).
      recommendedDaily: number;
      recommendedDailyEvent: number;
      recommendedHourlyChauffeured: number;
      minDailySelfDrive: number;
      minDailyEvent: number;
      minHourlyChauffeured: number;
      maxDailySelfDrive: number;
      maxDailyEvent: number;
      maxHourlyChauffeured: number;
      // Legacy backwards-compat fields (still consumed by admin Market
      // Data tab; new code should prefer `recommendedDaily`).
      rateLow: number;
      rateMid: number;
      rateHigh: number;
      monthlyEstimate: { low: number; mid: number; high: number };
      // Shared metadata.
      confidence: "high" | "medium" | "low";
      comparableCount: number;
      bookingCount: number;
      // Realized-price signal (task #1590). `realizedBookingCount` is the
      // number of past bookings of similar cars that fed the recommendation
      // (0 when no realized-price evidence). `realizedMedian` is null when
      // the signal didn't have enough samples to form a median.
      realizedBookingCount: number;
      realizedMedian: number | null;
      apexScore: number;
      factors: Array<{ factor: string; impact: string; detail: string }>;
    }>(`/ai-enrichment/suggested-rate/${carId}`, token),

  submitValuation: (carId: number, token: string, action: "accept" | "custom" | "refuse", proposed_value?: number,
    coverage?: { policy_version: string; acknowledge_partial_coverage: boolean; expected_revision: number; expected_updated_at: string }) =>
    authFetch<{
      success: boolean;
      status?: string;
      valuation_status?: string;
      insured_value?: number | string | null;
      car_value_range_id?: number | null;
    }>(`/listings/${carId}/valuation`, token, {
      method: "PUT",
      body: JSON.stringify({ action, proposed_value, ...coverage }),
    }),

  getCarBookings: (carId: number, token: string) =>
    authFetch<{ bookings: Array<{ id: number; publicId: string; startDate: string; endDate: string; status: string; tripType: string; renterFirstName?: string | null; renterAvatar?: string | null }> }>(`/listings/${carId}/bookings`, token),

  autoEnhancePhoto: (carId: number, token: string, imageBase64: string) =>
    authFetch<{ adjustments: { brightness: number; contrast: number; saturation: number; warmth: number; sharpness: number }; remaining?: number }>(
      `/listings/${carId}/photos/auto-enhance`,
      token,
      { method: "POST", body: JSON.stringify({ imageBase64 }) }
    ),
};
